import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import { type PropsWithChildren, useMemo } from "react";

import {
  type InvoiceRowData,
  TableInstanceProvider,
  useTableInstanceContext as _useTableInstanceContext,
} from "@/components/shared/data-table";
import useGlobalFilters from "@/components/shared/tables/globalFilterPersistence";
import type { RawFilters, ServerFilters } from "@/hooks/useQueryFilters";
import type { InvoiceFilterFields } from "@/types/utils";

import type { UseGetAllInvoicesData } from "@/queries/invoices/get-all";
import { columns as _columns } from "./columns";

export function useDocumentSotringTableInstance() {
  return _useTableInstanceContext<InvoiceRowData>();
}

const columnVisibility = {
  ocrReviewed: false,
  isBooking: false,
  isDuplicate: false,
  status: false,
};

type Props = PropsWithChildren<{
  tableName: string;
  data: UseGetAllInvoicesData;
  showConfidences?: boolean;
  clickableColumns?: string[];
  onRowReview: (invoiceIds: string[]) => void;
  onRowDelete: (invoiceIds: string[]) => void;
  onRowIsABooking: (invoiceId: string) => Promise<void>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  filters: RawFilters<InvoiceFilterFields>;
  resetFilters: () => void;
  initialServerSideFilters: ServerFilters<InvoiceFilterFields>;
  serverSideFilters: ServerFilters<InvoiceFilterFields>;
  globalServerSideSearch: string;
}>;

export const DocumentSortingTableInstanceProvider = ({
  data,
  children,
  tableName,
  clickableColumns = [
    "supplierName",
    "createdAt",
    "tags",
    "documentTypeSummary",
  ],
  onRowReview,
  onRowDelete,
  onRowIsABooking,
  sorting,
  onSortingChange,
  filters,
  resetFilters,
  initialServerSideFilters,
  serverSideFilters,
  globalServerSideSearch,
}: Props) => {
  const { globalFilter, onGlobalFilterChange } = useGlobalFilters(tableName);

  const handleRowReview = (invoiceId: string) => {
    onRowReview([invoiceId]);
  };

  const handleRowDelete = (invoiceId: string) => {
    onRowDelete([invoiceId]);
  };

  const columns = useMemo(() => {
    return _columns(handleRowReview, handleRowDelete, onRowIsABooking);
  }, []);

  const invoices = data?.map((invoice) => invoice.data) ?? [];

  const columnFilters = useMemo(() => {
    return Object.entries(filters).map(([key, value]) => ({
      id: key,
      value: value,
    }));
  }, [filters]);

  return (
    <TableInstanceProvider
      tableName={tableName}
      data={invoices}
      columns={columns}
      clickableColumns={clickableColumns}
      initialColumnVisibility={columnVisibility}
      getRowId={(row) => row.invoiceId}
      columnFilters={columnFilters}
      globalFilter={globalFilter}
      onGlobalFilterChange={onGlobalFilterChange}
      onColumnFilterChange={() => {}}
      manualFiltering={false}
      manualPagination={true}
      manualSorting={true}
      sorting={sorting}
      onSortingChange={onSortingChange}
      initialServerSideFilters={initialServerSideFilters}
      serverSideFilters={serverSideFilters}
      onServerSideFiltersClear={resetFilters}
      globalServerSideSearch={globalServerSideSearch}
    >
      {children}
    </TableInstanceProvider>
  );
};
